import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function onlyUniqueById(value: any, index: any, self: any) {
  return self.findIndex((item: any) => item.id === value.id) === index
}

export function formatDate(input: string | number | Date): string {
  const date = new Date(input)
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

export const generateRandomString = (length: number, lowercase = false) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXY3456789' // excluding similar looking characters like Z, 2, I, 1, O, 0
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return lowercase ? result.toLowerCase() : result
}

interface languageMap {
  [key: string]: string | undefined
}

export const programmingLanguages: languageMap = {
  javascript: '.js',
  python: '.py',
  java: '.java',
  c: '.c',
  cpp: '.cpp',
  'c++': '.cpp',
  'c#': '.cs',
  ruby: '.rb',
  php: '.php',
  swift: '.swift',
  'objective-c': '.m',
  kotlin: '.kt',
  typescript: '.ts',
  go: '.go',
  perl: '.pl',
  rust: '.rs',
  scala: '.scala',
  haskell: '.hs',
  lua: '.lua',
  shell: '.sh',
  sql: '.sql',
  html: '.html',
  css: '.css'
}

export function isEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true
  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null)
    return false
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  if (keys1.length !== keys2.length) return false
  for (const key of keys1) {
    if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) return false
  }
  return true
}

export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - Safari specific check
    (window.DocumentTouch && document instanceof DocumentTouch)
  )
}

/**
 * Escapes apostrophes in a string for React JSX compatibility
 * Replaces single quotes with HTML entities to avoid React linter warnings
 */
export const escapeApostrophes = (text: string): string => {
  if (!text || typeof text !== 'string') return text
  return text.replace(/'/g, '&#39;')
}

function parseCsvEnv(value: string | undefined): string[] {
  if (!value) return []

  return value
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/**
 * Check if a user is a super admin based on their email
 * @param email The email to check
 * @returns A boolean indicating if the user is a super admin
 */
export function isSuperAdmin(email: string | undefined | null): boolean {
  if (!email) {
    return false
  }

  const normalizedEmail = normalizeEmail(email)

  // Get super admin email from environment variable or use default
  const superAdminEmail = process.env.SUPER_ADMIN_EMAIL || 'ayoelutilo@gmail.com'
  const normalizedSuperAdminEmail = superAdminEmail.trim().toLowerCase()

  return normalizedEmail === normalizedSuperAdminEmail
}

/**
 * Check if a user belongs to the Marshmallow internal team.
 * Supports explicit allowlists and domain-based access for internal tools.
 */
export function isInternalTeamMember(email: string | undefined | null): boolean {
  if (!email) {
    return false
  }

  if (isSuperAdmin(email)) {
    return true
  }

  const normalizedEmail = normalizeEmail(email)
  const allowedEmails = parseCsvEnv(
    process.env.NEXT_PUBLIC_MARSHMALLOW_INTERNAL_EMAILS ||
      process.env.MARSHMALLOW_INTERNAL_EMAILS
  )
  const allowedDomains = parseCsvEnv(
    process.env.NEXT_PUBLIC_MARSHMALLOW_INTERNAL_DOMAINS ||
      process.env.MARSHMALLOW_INTERNAL_DOMAINS ||
      'marshmallow.studio,marshmallowstudio.com,losi.ai'
  )

  if (allowedEmails.includes(normalizedEmail)) {
    return true
  }

  return allowedDomains.some((domain) => normalizedEmail.endsWith(`@${domain}`))
}
